import {
  FileText,
  Mail,
  LogOut,
  ChevronDown,
  UserCog,
  Home,
  Plus,
  List,
  Users,
  UserPlus,
  Settings,
} from "lucide-react";
import { motion } from "framer-motion";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import favIcon from "@/assets/favIcon.webp";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const mainNavItems = [
  {
    title: "Resources",
    icon: FileText,
    subItems: [
      { title: "All Posts", url: "/dashboard/blogs", icon: List },
      { title: "Create Post", url: "/dashboard/blogs/create", icon: Plus },
    ],
  },
  {
    title: "Accounts",
    icon: Users,
    subItems: [{ title: "Registrations", url: "/dashboard/registrations", icon: UserPlus }],
  },
  {
    title: "Newsletter",
    icon: Mail,
    subItems: [{ title: "Subscribers", url: "/dashboard/newsletter", icon: Users }],
  },
];

export function AppSidebar() {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const roleLabel =
    user?.role === "admin" ? "Admin" : user?.role === "clients" ? "Client" : user?.role || "User";

  // 1. Initialize state as an array to hold multiple open menu titles.
  // It automatically checks if any sub-item is active on the initial load.
  const [openMenus, setOpenMenus] = useState(() => {
    const activeItem = mainNavItems.find((item) =>
      item.subItems?.some(
        (sub) => location.pathname === sub.url || location.pathname.startsWith(`${sub.url}/`)
      )
    );
    return activeItem ? [activeItem.title] : [];
  });

  const [hoveredItem, setHoveredItem] = useState(null);

  // 2. Automatically open a menu if the user navigates to its route from elsewhere
  useEffect(() => {
    const activeItem = mainNavItems.find((item) =>
      item.subItems?.some(
        (sub) => location.pathname === sub.url || location.pathname.startsWith(`${sub.url}/`)
      )
    );
    if (activeItem && !openMenus.includes(activeItem.title)) {
      setOpenMenus((prev) => [...prev, activeItem.title]);
    }
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully", {
      description: "You have been logged out of your account.",
      duration: 3000,
    });
  };

  // 3. Toggle logic now adds or removes the title from the array
  const toggleSubmenu = (title) => {
    setOpenMenus((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const handleMobileClose = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const renderNavItem = (item) => {
    const baseClasses =
      "flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-lg transition-all duration-200 hover:scale-[1.02] rounded-lg group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0";

    // Submenu Items
    if (item.subItems) {
      // 4. Check if this specific item's title is in the openMenus array
      const isOpen = openMenus.includes(item.title);

      const isAnySubItemActive = item.subItems.some(
        (sub) => location.pathname === sub.url || location.pathname.startsWith(`${sub.url}/`)
      );

      let parentClasses = baseClasses;
      if (isOpen || isAnySubItemActive) {
        parentClasses += " bg-white/10 font-semibold text-white shadow-lg";
      } else {
        parentClasses += " font-medium text-white/80 hover:bg-white/5 hover:text-white";
      }

      return (
        <SidebarMenuItem key={item.title}>
          <div
            onMouseEnter={() => setHoveredItem(item.title)}
            onMouseLeave={() => setHoveredItem(null)}>
            <SidebarMenuButton
              tooltip={collapsed ? item.title : undefined}
              onClick={() => toggleSubmenu(item.title)}
              className={parentClasses}>
              <item.icon className="h-7 w-7 shrink-0" />

              <span className="flex-1 text-left text-lg transition-all duration-200 group-data-[state=collapsed]:hidden">
                {item.title}
              </span>

              <ChevronDown
                className={`ml-auto h-5 w-5 shrink-0 transition-transform duration-200 group-data-[state=collapsed]:hidden ${
                  isOpen ? "rotate-180" : "rotate-0"
                }`}
              />
            </SidebarMenuButton>

            {isOpen && (
              <SidebarMenuSub className="ml-7 border-l border-white/20 pl-4 transition-all duration-200 group-data-[state=collapsed]:hidden">
                {item.subItems.map((subItem) => {
                  const isSubActive =
                    subItem.url === "/dashboard/blogs"
                      ? location.pathname === "/dashboard/blogs" ||
                        location.pathname.startsWith("/dashboard/blogs/edit/")
                      : location.pathname === subItem.url ||
                        location.pathname.startsWith(`${subItem.url}/`);

                  return (
                    <SidebarMenuSubItem key={subItem.url}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={isSubActive}
                        className="h-auto py-1.5">
                        <NavLink
                          to={subItem.url}
                          onClick={handleMobileClose}
                          className={`${
                            isSubActive
                              ? "bg-white/15 font-semibold text-white shadow-md"
                              : "text-white/70 hover:bg-white/5 hover:text-white"
                          } flex w-full cursor-pointer items-center gap-4 rounded-md px-3 py-3 text-base transition-all duration-200 hover:translate-x-1`}>
                          <subItem.icon className="h-6 w-6 shrink-0" />
                          <span>{subItem.title}</span>
                        </NavLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  );
                })}
              </SidebarMenuSub>
            )}
          </div>
        </SidebarMenuItem>
      );
    }

    // Standalone Items
    const isItemActive = isActive(item.url);
    let linkClasses = baseClasses;

    if (isItemActive) {
      linkClasses += " bg-white/10 font-semibold text-white shadow-lg";
    } else if (hoveredItem === item.title) {
      linkClasses += " bg-white/5 font-medium text-white";
    } else {
      linkClasses += " font-medium text-white/80 hover:bg-white/5 hover:text-white";
    }

    return (
      <SidebarMenuItem key={item.title}>
        <div
          onMouseEnter={() => setHoveredItem(item.title)}
          onMouseLeave={() => setHoveredItem(null)}>
          <SidebarMenuButton
            asChild
            isActive={isItemActive}
            tooltip={collapsed ? item.title : undefined}>
            <NavLink
              to={item.url}
              end={item.url === "/dashboard"}
              onClick={handleMobileClose}
              className={linkClasses}>
              <item.icon className="h-7 w-7 shrink-0" />
              <span className="text-lg transition-all duration-200 group-data-[state=collapsed]:hidden">
                {item.title}
              </span>
            </NavLink>
          </SidebarMenuButton>
        </div>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-blue-900/50 bg-linear-to-b from-blue-950 to-blue-900">
      <SidebarHeader className="p-4 transition-all duration-200 group-data-[state=collapsed]:px-2 group-data-[state=collapsed]:py-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          onClick={() => {
            navigate("/");
            handleMobileClose();
          }}
          className="flex cursor-pointer items-center gap-4 rounded-lg px-3 py-3 transition-all duration-200 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0 hover:bg-white/5">
          <div className="flex shrink-0 items-center justify-center">
            <img
              src={favIcon}
              alt="VetAssist Logo"
              className="h-14 w-14 rounded-full object-contain transition-all duration-200 group-data-[state=collapsed]:h-12 group-data-[state=collapsed]:w-12"
            />
          </div>

          <div className="flex min-w-0 flex-col overflow-hidden transition-all duration-200 group-data-[state=collapsed]:hidden">
            <span className="truncate text-xl font-bold text-white">VetAssist</span>
            <span className="truncate text-sm text-white/60">Clinic Management</span>
          </div>
        </motion.div>
      </SidebarHeader>

      <SidebarSeparator className="bg-white/10" />

      <SidebarContent className="px-3 py-2 transition-all duration-200 group-data-[state=collapsed]:px-2">
        <SidebarGroup className="transition-all duration-200 group-data-[state=collapsed]:px-0">
          <SidebarGroupLabel className="px-4 py-3 text-sm font-bold tracking-wider text-white/40 uppercase transition-all duration-200 group-data-[state=collapsed]:hidden">
            Main Menu
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-2">
              {mainNavItems.map((item) => renderNavItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator className="bg-white/10" />

      <SidebarFooter className="mt-auto p-4 transition-all duration-200 group-data-[state=collapsed]:px-2 group-data-[state=collapsed]:py-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex w-full cursor-pointer items-center gap-4 rounded-lg px-3 py-3 text-left transition-all duration-200 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0 hover:bg-white/10 hover:shadow-lg">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/20 backdrop-blur-sm transition-all duration-200 group-data-[state=collapsed]:h-12 group-data-[state=collapsed]:w-12">
                    <UserCog className="h-8 w-8 text-white transition-all duration-200 group-data-[state=collapsed]:h-7 group-data-[state=collapsed]:w-7" />
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col overflow-hidden transition-all duration-200 group-data-[state=collapsed]:hidden">
                    <span className="truncate text-lg font-semibold text-white">
                      {user?.name ?? "User"}
                    </span>
                    <span className="truncate text-sm text-white/60">{roleLabel}</span>
                  </div>
                  <ChevronDown className="h-5 w-5 shrink-0 text-white/60 transition-all duration-200 group-data-[state=collapsed]:hidden" />
                </motion.button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 border-blue-800 bg-blue-950">
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/dashboard/account-settings");
                    handleMobileClose();
                  }}
                  className="cursor-pointer py-4 text-lg text-white hover:bg-white/10 focus:bg-white/10 focus:text-white">
                  <Settings className="mr-4 h-6 w-6" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/");
                    handleMobileClose();
                  }}
                  className="cursor-pointer py-4 text-lg text-white hover:bg-white/10 focus:bg-white/10 focus:text-white">
                  <Home className="mr-4 h-6 w-6" />
                  Home
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    handleLogout();
                    handleMobileClose();
                  }}
                  className="cursor-pointer py-4 text-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 focus:bg-red-500/10 focus:text-red-300">
                  <LogOut className="mr-4 h-6 w-6" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
